import React, { useCallback, useEffect, useState } from 'react';
import { Check, ExternalLink, Store, X } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import {
  storefrontsApi,
  waitingFor,
  REVIEW_STATUS_LABEL,
  StorefrontForReview,
  StorefrontReviewStatus,
} from '../../api/storefronts';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { PageHeader } from '../../components/ui/PageHeader';
import { Spinner } from '../../components/ui/Spinner';
import { ImageLightbox } from '../../components/ui/ImageLightbox';

/** The server's floor. Mirrored so the button can explain itself before the
 *  request rather than after it comes back rejected. */
const MIN_REASON = 10;

const formatNgn = (n: number) =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n);

const TABS: StorefrontReviewStatus[] = [
  'in_review',
  'approved',
  'rejected',
  'draft',
];

/**
 * Deciding which business storefronts reach the consumer marketplace.
 *
 * The queue exists because a verified business is not the same claim as a safe
 * shop: KYB says the company is real, and says nothing about what this shop is
 * selling or what the page claims about it. Whoever works this queue is the
 * only person who checks that second thing on the shopper's behalf.
 */
export const StorefrontReview: React.FC = () => {
  usePage('Storefront Review');
  const toast = useToast();

  const [tab, setTab] = useState<StorefrontReviewStatus>('in_review');
  const [rows, setRows] = useState<StorefrontForReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<StorefrontForReview | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await storefrontsApi.queue(tab));
    } catch (err) {
      toast.error(err, 'Could not load storefronts.');
    } finally {
      setLoading(false);
    }
  }, [tab, toast]);

  useEffect(() => {
    void fetchQueue();
  }, [fetchQueue]);

  const closeModal = () => {
    setActive(null);
    setReason('');
    setLightboxIndex(null);
  };

  const decide = async (verdict: 'approve' | 'reject') => {
    if (!active) return;
    if (verdict === 'reject' && reason.trim().length < MIN_REASON) return;

    setSaving(true);
    try {
      if (verdict === 'approve') {
        await storefrontsApi.approve(active.id);
        toast.success(
          `${active.name} approved. Its owner can now switch it on.`,
        );
      } else {
        await storefrontsApi.reject(active.id, reason.trim());
        toast.success(`${active.name} rejected. The reason was sent back.`);
      }
      closeModal();
      await fetchQueue();
    } catch (err) {
      toast.error(err, 'Could not record that decision.');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Shop',
      render: (r: StorefrontForReview) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
            {r.tagline || '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'url',
      header: 'Address',
      render: (r: StorefrontForReview) => (
        <a
          href={r.url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--blue-600)',
            fontSize: 13,
          }}
        >
          /{r.slug}
          <ExternalLink size={13} />
        </a>
      ),
    },
    {
      key: 'waiting',
      header: 'Waiting',
      render: (r: StorefrontForReview) => (
        <span style={{ fontSize: 13 }}>{waitingFor(r.submittedAt)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r: StorefrontForReview) => (
        <Badge
          variant={
            r.reviewStatus === 'approved'
              ? 'success'
              : r.reviewStatus === 'rejected'
                ? 'danger'
                : 'warning'
          }
        >
          {REVIEW_STATUS_LABEL[r.reviewStatus]}
        </Badge>
      ),
    },
  ];

  const reasonTooShort = reason.trim().length < MIN_REASON;

  return (
    <div>
      <PageHeader
        title="Storefront Review"
        description="Business shops waiting to be listed on the marketplace"
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {TABS.map((t) => (
          <Button
            key={t}
            variant={t === tab ? 'primary' : 'secondary'}
            onClick={() => setTab(t)}
          >
            {REVIEW_STATUS_LABEL[t]}
          </Button>
        ))}
      </div>

      <Card>
        {loading ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
            <Spinner />
          </div>
        ) : (
          <Table
            columns={columns}
            data={rows}
            onRowClick={(r) => setActive(r)}
            emptyMessage={
              tab === 'in_review'
                ? 'Nothing waiting to be reviewed'
                : `No ${REVIEW_STATUS_LABEL[tab].toLowerCase()} storefronts`
            }
          />
        )}
      </Card>

      <Modal
        isOpen={active !== null}
        onClose={closeModal}
        title={active?.name ?? ''}
        maxWidth={640}
      >
        {active ? (
          <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: 12,
                background: 'var(--neutral-200)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <Store size={16} color="var(--neutral-600)" />
              <a
                href={active.url}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--blue-600)', fontSize: 14 }}
              >
                {active.url}
              </a>
            </div>

            {(active.branding.logoUrl || active.branding.coverUrl) && (
              <div style={{ display: 'flex', gap: 12 }}>
                {active.branding.logoUrl && (
                  <img
                    src={active.branding.logoUrl}
                    alt="Shop logo"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 'var(--radius-md)',
                      objectFit: 'cover',
                      border: '1px solid var(--neutral-300)',
                    }}
                  />
                )}
                {active.branding.coverUrl && (
                  <img
                    src={active.branding.coverUrl}
                    alt="Shop cover"
                    style={{
                      flex: 1,
                      height: 56,
                      borderRadius: 'var(--radius-md)',
                      objectFit: 'cover',
                      border: '1px solid var(--neutral-300)',
                    }}
                  />
                )}
              </div>
            )}

            <Field label="Category" value={active.category} />
            <Field label="Tagline" value={active.tagline} />
            <Field label="Description" value={active.description} />

            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--neutral-500)',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Contact
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                }}
              >
                <Field label="Email" value={active.contact.email} />
                <Field label="Phone" value={active.contact.phone} />
                <Field label="WhatsApp" value={active.contact.whatsapp} />
                <Field label="Address" value={active.contact.address} />
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--neutral-500)',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Catalogue · {active.productCount}{' '}
                {active.productCount === 1 ? 'product' : 'products'}
              </div>
              {active.productPreview.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
                  Nothing listed yet.
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {(() => {
                    // Only the products with a photo are worth expanding —
                    // this is what prev/next in the lightbox cycles through,
                    // so an image-less product just isn't part of it.
                    const productImages = active.productPreview
                      .map((p) => p.imageUrl)
                      .filter((url): url is string => Boolean(url));
                    return active.productPreview.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        width: 120,
                        border: '1px solid var(--neutral-300)',
                        borderRadius: 'var(--radius-md)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        onClick={
                          p.imageUrl
                            ? () =>
                                setLightboxIndex(productImages.indexOf(p.imageUrl as string))
                            : undefined
                        }
                        style={{
                          width: '100%',
                          height: 80,
                          background: 'var(--neutral-200)',
                          cursor: p.imageUrl ? 'zoom-in' : 'default',
                        }}
                      >
                        {p.imageUrl && (
                          <img
                            src={p.imageUrl}
                            alt={p.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        )}
                      </div>
                      <div style={{ padding: 8 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {p.name}
                        </div>
                        <div
                          style={{ fontSize: 12, color: 'var(--neutral-600)' }}
                        >
                          {formatNgn(p.price)}
                        </div>
                      </div>
                    </div>
                    ));
                  })()}
                  {active.productCount > active.productPreview.length && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 12,
                        color: 'var(--neutral-500)',
                        padding: '0 4px',
                      }}
                    >
                      +{active.productCount - active.productPreview.length}{' '}
                      more — see the full catalogue at the shop's address
                      above.
                    </div>
                  )}
                </div>
              )}
            </div>

            <Field
              label="Submitted"
              value={
                active.submittedAt
                  ? `${new Date(active.submittedAt).toLocaleString()} (${waitingFor(active.submittedAt)} ago)`
                  : null
              }
            />

            {active.reviewStatus === 'in_review' ? (
              <>
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 13,
                      fontWeight: 600,
                      marginBottom: 6,
                    }}
                  >
                    Reason, if rejecting
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder="What does the seller need to change before resubmitting?"
                    style={{
                      width: '100%',
                      padding: 10,
                      fontSize: 14,
                      fontFamily: 'inherit',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--neutral-300)',
                      resize: 'vertical',
                    }}
                  />
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--neutral-500)',
                      marginTop: 4,
                    }}
                  >
                    Sent to the seller word for word, so it has to say what to
                    fix. Not needed to approve.
                  </div>
                </div>

                <div
                  style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
                >
                  <Button
                    variant="danger"
                    disabled={saving || reasonTooShort}
                    onClick={() => void decide('reject')}
                  >
                    <X size={15} />
                    {reasonTooShort ? 'Add a reason to reject' : 'Reject'}
                  </Button>
                  <Button
                    variant="primary"
                    disabled={saving}
                    onClick={() => void decide('approve')}
                  >
                    <Check size={15} />
                    Approve
                  </Button>
                </div>

                {/* Worth stating on the screen where the decision is made:
                    approving grants the owner the right to switch the shop on,
                    it does not open it for them. */}
                <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                  Approving does not make the shop live — its owner switches it
                  on when they are ready.
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
                This storefront is {REVIEW_STATUS_LABEL[
                  active.reviewStatus
                ].toLowerCase()} and is not awaiting a decision.
              </div>
            )}
          </div>
          {lightboxIndex !== null && (
            <ImageLightbox
              images={active.productPreview
                .map((p) => p.imageUrl)
                .filter((url): url is string => Boolean(url))}
              index={lightboxIndex}
              onIndexChange={setLightboxIndex}
              onClose={() => setLightboxIndex(null)}
            />
          )}
          </>
        ) : null}
      </Modal>
    </div>
  );
};

const Field: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <div>
    <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: 14, color: 'var(--neutral-800)' }}>
      {value || <span style={{ color: 'var(--neutral-500)' }}>Not set</span>}
    </div>
  </div>
);
